// Define the Project type
export interface Project {
  project_id: string;
  project_name: string;
  project_start_date: string;
  created_by_user_id?: string;
  project_level: "Company" | "Business Unit" | "System";
  company_id?: string;
  bu_id?: string;
  system_id?: string;
  project_end_date?: string;
  project_status: "Active" | "Hold" | "Completed" | "Yet to Start";
  assessment_scope?: any;
}
// Define the System type
export interface System {
  system_name: string;
  system_id: string;
  owner_first_name?: string;
  owner_last_name?: string;
  email: string;
  system_type?: "IT" | "OT" | "Product";
  shelf_life: number;
  business_criticality?: "Very High" | "High" | "Medium" | "Low" | "Very Low";
  upgradability?:
    | "Very Easy"
    | "Easy"
    | "Moderate"
    | "Difficult"
    | "Very Difficult";
  crypto_agility?:
    | "L0: Initial"
    | "L1: Developing"
    | "L2: Defined"
    | "L3: Managed"
    | "L4: Optimized";
  // associated_amdb_table?: string;
  // associated_cmdb_table?: string;
  // associated_data_assets_table?: string;
  location?: string;
  time_for_migration: number;
  risk_tolerance?: "Very Low" | "Low" | "Moderate" | "High" | "Very High";
  regulatory_compliance?: string;
  // supplied_by?: string;
  vendor_supplied_asset?: "Yes" | "No";
  // bu_id : string;
}
// Define the state structure
export interface ProjectState {
  projects: Project[];
  project_name: string | null;
  company_id: string | null | undefined;
  bu_id: string | null | undefined;
  system_id: string | null;
  selected_project: Project | null; //to track current project
  project_status: "Active" | "Hold" | "Completed" | "Yet to Start" | null;
  project_level: "Company" | "Business Unit" | "System" | null;
  currentProjectID: string | null; // Initialize as null
  currentProjectSystemInfo: System | null; //to track system info of current project
  newProject: string | null;
  isAmdbPresent: boolean;
  isCmdbPresent: boolean;
  isDataAssetPresent: boolean;
  showAmdb: boolean;
  showCmdb: boolean;
  showDataAsset: boolean;
}

// Initial state
export const initialProjectState: ProjectState = {
  projects: [],
  project_name: null,
  company_id: null,
  bu_id: null,
  system_id: null,
  selected_project: null,
  project_status: null,
  project_level: null,
  currentProjectID: null, // Initialize as null
  currentProjectSystemInfo: null,
  newProject: null,
  isAmdbPresent: false,
  isCmdbPresent: false,
  isDataAssetPresent: false,
  showAmdb: false,
  showCmdb: false,
  showDataAsset: false,
};

// Define the action types
export type ProjectAction =
  | { type: "ADD_PROJECT"; payload: Project }
  | { type: "UPDATE_PROJECT"; payload: Project }
  | { type: "DELETE_PROJECT"; payload: string }
  | { type: "SET_PROJECTS"; payload: Project[] }
  | { type: "SET_COMPANY_ID"; payload: string | null | undefined }
  | { type: "SET_BU_ID"; payload: string | null | undefined }
  | { type: "SET_SYSTEM_ID"; payload: string }
  | { type: "SET_SELECTED_PROJECT"; payload: Project | null }
  | { type: "SET_PROJECT_NAME"; payload: string }
  | { type: "SET_CURRENT_PROJECT_ID"; payload: string }
  | { type: "SET_CURRENT_PROJECT_SYSTEM_INFO"; payload: System | null }
  | { type: "SET_NEW_PROJECT"; payload: string | null }
  | { type: "SET_IS_AMDB_PRESENT"; payload: boolean }
  | { type: "SET_IS_CMDB_PRESENT"; payload: boolean }
  | { type: "SET_IS_DATA_ASSET_PRESENT"; payload: boolean } // New action for setting currentProject
  | { type: "SET_SHOW_AMDB"; payload: boolean }
  | { type: "SET_SHOW_CMDB"; payload: boolean }
  | { type: "SET_SHOW_DATA_ASSET"; payload: boolean }
  | { type: "SET_PROJECT_SCOPE"; payload: any }; // New action for setting project scope;

// Reducer function
export const projectReducer = (
  state: ProjectState,
  action: ProjectAction
): ProjectState => {
  switch (action.type) {
    case "ADD_PROJECT":
      return { ...state, projects: [...state.projects, action.payload] };
    case "SET_PROJECT_NAME":
      return { ...state, project_name: action.payload };
    case "UPDATE_PROJECT":
      return {
        ...state,
        projects: state.projects.map((project) =>
          project.project_id === action.payload.project_id
            ? action.payload
            : project
        ),
      };
    case "DELETE_PROJECT":
      return {
        ...state,
        projects: state.projects.filter(
          (project) => project.project_id !== action.payload
        ),
      };
    case "SET_PROJECTS":
      return { ...state, projects: action.payload };
    case "SET_COMPANY_ID":
      return { ...state, company_id: action.payload };
    case "SET_BU_ID":
      // return { ...state, bu_id: action.payload };
      return {
        ...state,
        bu_id: action.payload ?? null,
      };
    case "SET_SYSTEM_ID":
      return { ...state, system_id: action.payload };
    case "SET_SELECTED_PROJECT":
      return { ...state, selected_project: action.payload };
    case "SET_CURRENT_PROJECT_ID": // Handle the action to set the currentProject
      return { ...state, currentProjectID: action.payload };
    case "SET_CURRENT_PROJECT_SYSTEM_INFO": // Handle the action to set the currentProject system info
      return { ...state, currentProjectSystemInfo: action.payload };
    case "SET_NEW_PROJECT":
      return { ...state, newProject: action.payload };
    case "SET_IS_AMDB_PRESENT":
      return { ...state, isAmdbPresent: action.payload };
    case "SET_IS_CMDB_PRESENT":
      return { ...state, isCmdbPresent: action.payload };
    case "SET_IS_DATA_ASSET_PRESENT":
      return { ...state, isDataAssetPresent: action.payload };
    case "SET_SHOW_AMDB":
      return { ...state, showAmdb: action.payload };
    case "SET_SHOW_CMDB":
      return { ...state, showCmdb: action.payload };
    case "SET_SHOW_DATA_ASSET":
      return { ...state, showDataAsset: action.payload };
    case "SET_PROJECT_SCOPE":
      return {
        ...state,
        selected_project: state.selected_project
          ? { ...state.selected_project, assessment_scope: action.payload }
          : null,
      };
    default:
      throw new Error(`Unhandled action type: ${action}`);
  }
};
